/*
 * RemindersPresets — the "Presets" control in the filters row.
 *
 * A preset is a named snapshot of the current filter values. This control:
 *   - lists saved presets; clicking a name applies its filters (onApply)
 *   - toggles a preset as the default (star) — at most one default
 *   - deletes a preset (trash, with an inline confirm)
 *   - saves the current filters as a new preset, or updates an existing one
 *     when the typed name matches (case-insensitive)
 *
 * Styling reuses the tokenised popover/button helpers from reminders.css so it
 * themes in both light and dark like the SearchableSelect dropdown.
 */

import React, { useEffect, useRef, useState } from 'react';
import {
  FaChevronDown,
  FaStar,
  FaRegStar,
  FaTrashAlt,
  FaPlus,
  FaCheck,
  FaTimes
} from 'react-icons/fa';
import { readPresets, writePresets, makePresetId } from './presets';
import { PERSISTED_FILTER_KEYS } from './constants';
import './reminders.css';

const triggerCls = 'reminders-trigger-btn flex w-full items-center justify-between gap-2 text-sm';

function RemindersPresets({ currentFilters, onApply }) {
  const [presets, setPresets] = useState(() => readPresets());
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('list'); // 'list' | 'save'
  const [nameInput, setNameInput] = useState('');
  const [makeDefault, setMakeDefault] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const rootRef = useRef(null);
  const inputRef = useRef(null);

  const closeAll = () => {
    setOpen(false);
    setMode('list');
    setNameInput('');
    setMakeDefault(false);
    setPendingDeleteId(null);
  };

  // Close on outside click / escape.
  useEffect(() => {
    if (!open) return undefined;
    const handleClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) closeAll();
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') closeAll();
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  useEffect(() => {
    if (open && mode === 'save' && inputRef.current) inputRef.current.focus();
  }, [open, mode]);

  const persist = (next) => {
    setPresets(next);
    writePresets(next);
  };

  const trimmed = nameInput.trim();
  const existing = presets.find((p) => p.name.toLowerCase() === trimmed.toLowerCase()) || null;

  // Snapshot the current filter subset (only the persisted keys).
  const captureCurrent = () => {
    const f = {};
    PERSISTED_FILTER_KEYS.forEach((k) => {
      f[k] = currentFilters[k];
    });
    return f;
  };

  const handleSave = () => {
    if (!trimmed) return;
    const filters = captureCurrent();
    let next;
    let targetId;
    if (existing) {
      targetId = existing.id;
      next = presets.map((p) =>
        p.id === existing.id ? { ...p, filters, isDefault: makeDefault || p.isDefault } : p
      );
    } else {
      targetId = makePresetId();
      next = [...presets, { id: targetId, name: trimmed, isDefault: makeDefault, filters }];
    }
    // Enforce at-most-one default: if this one is default, clear the others.
    if (makeDefault) {
      next = next.map((p) => ({ ...p, isDefault: p.id === targetId }));
    }
    persist(next);
    closeAll();
  };

  const handleApply = (preset) => {
    onApply(preset.filters);
    closeAll();
  };

  const toggleDefault = (id) => {
    const target = presets.find((p) => p.id === id);
    const makingDefault = !(target && target.isDefault);
    persist(presets.map((p) => ({ ...p, isDefault: makingDefault && p.id === id })));
  };

  const handleDelete = (id) => {
    persist(presets.filter((p) => p.id !== id));
    setPendingDeleteId(null);
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        className={triggerCls}
        onClick={() => (open ? closeAll() : setOpen(true))}
      >
        <span className="truncate">Presets{presets.length ? ` (${presets.length})` : ''}</span>
        <FaChevronDown size={9} className="text-content-muted" />
      </button>

      {open ? (
        <div className="reminders-popover absolute left-0 z-50 mt-1 w-[min(320px,calc(100vw-40px))] overflow-hidden rounded-md">
          {mode === 'list' ? (
            <div className="max-h-72 overflow-y-auto py-1">
              {presets.length === 0 ? (
                <div className="px-3 py-2 text-xs text-content-muted">No saved presets yet</div>
              ) : (
                presets.map((p) => (
                  <div key={p.id} className="flex w-full items-center gap-1 px-1.5 py-0.5">
                    <button
                      type="button"
                      onClick={() => toggleDefault(p.id)}
                      title={p.isDefault ? 'Default preset (applies on load)' : 'Set as default'}
                      aria-label={p.isDefault ? 'Unset default' : 'Set as default'}
                      className="reminders-icon-btn shrink-0"
                      style={{ padding: 4 }}
                    >
                      {p.isDefault ? (
                        <FaStar size={12} className="text-brand-primary" />
                      ) : (
                        <FaRegStar size={12} className="text-content-muted" />
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleApply(p)}
                      className="reminders-neutral-btn flex-1 truncate rounded px-2 py-1 text-left text-sm"
                    >
                      {p.name}
                    </button>
                    {pendingDeleteId === p.id ? (
                      <span className="flex shrink-0 items-center gap-0.5">
                        <button
                          type="button"
                          onClick={() => handleDelete(p.id)}
                          title="Confirm delete"
                          aria-label="Confirm delete"
                          className="reminders-icon-btn"
                          style={{ padding: 4 }}
                        >
                          <FaCheck size={11} className="text-state-error" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setPendingDeleteId(null)}
                          title="Cancel"
                          aria-label="Cancel delete"
                          className="reminders-icon-btn"
                          style={{ padding: 4 }}
                        >
                          <FaTimes size={11} className="text-content-muted" />
                        </button>
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setPendingDeleteId(p.id)}
                        title="Delete preset"
                        aria-label="Delete preset"
                        className="reminders-icon-btn shrink-0"
                        style={{ padding: 4 }}
                      >
                        <FaTrashAlt size={11} className="text-content-muted" />
                      </button>
                    )}
                  </div>
                ))
              )}
              <button
                type="button"
                onClick={() => {
                  setPendingDeleteId(null);
                  setMode('save');
                }}
                className="reminders-neutral-btn-create flex w-full items-center gap-2 text-left text-sm"
              >
                <FaPlus size={9} />
                Save current filters as preset
              </button>
            </div>
          ) : (
            <div className="p-2.5">
              <div className="mb-1.5 text-xs font-semibold text-content-secondary">Save preset</div>
              <input
                ref={inputRef}
                value={nameInput}
                onChange={(e) => setNameInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSave();
                }}
                placeholder="Preset name"
                className="reminders-preset-input mb-2 w-full text-sm"
              />
              {existing ? (
                <div className="mb-2 text-xs text-state-warning">
                  A preset named &ldquo;{existing.name}&rdquo; exists — saving will update it.
                </div>
              ) : null}
              <label
                htmlFor="reminders-preset-default"
                className="mb-2.5 flex items-center gap-2 text-xs text-content-secondary"
              >
                <input
                  id="reminders-preset-default"
                  type="checkbox"
                  checked={makeDefault}
                  onChange={(e) => setMakeDefault(e.target.checked)}
                />
                Set as default (applies on load)
              </label>
              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setMode('list');
                    setNameInput('');
                    setMakeDefault(false);
                  }}
                  className="reminders-secondary-btn text-xs"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={!trimmed}
                  className="reminders-primary-btn text-xs"
                >
                  {existing ? 'Update' : 'Save'}
                </button>
              </div>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

export default RemindersPresets;
